import { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedClass, setZoomLevel, setDragOverSlot, clearDraggedClass } from '../../store/slices/calendarSlice';
import { updateClass, updateClassLocal } from '../../store/slices/classesSlice';
import { daysOfWeek, timeSlots, isWeekend, isWeekdayMorning, formatTime, getDurationInSlots, isHalfHourTime, getBaseHourSlot, getHalfHourOffset, getHalfHourTime } from '../../utils/timeUtils';
import { getClassBackgroundColor, getClassBorderColor, getRumaalColor } from '../../utils/colors';
import ClassBlock from './ClassBlock';
import EmptySlot from './EmptySlot';
import WeeklyStats from '../dashboard/WeeklyStats';
import LocationSwitcher from '../location/LocationSwitcher';

// Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

// Helper function to get current week date range (Monday to Sunday)
const getCurrentWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days to subtract to get to Monday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysToMonday);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  const formatDate = (date) => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };
  
  const mondayFormatted = formatDate(monday);
  const sundayFormatted = formatDate(sunday);
  
  // If same month, show: "1st - 7th Dec"
  // If different months, show: "30th Nov - 6th Dec"
  if (monday.getMonth() === sunday.getMonth()) {
    return `${monday.getDate()}${getOrdinalSuffix(monday.getDate())} - ${sunday.getDate()}${getOrdinalSuffix(sunday.getDate())} ${monday.toLocaleDateString('en-US', { month: 'short' })}`;
  } else {
    return `${mondayFormatted} - ${sundayFormatted}`;
  }
};

// Helper function to get date for a specific day of the week (Monday = 0, Sunday = 6)
const getDateForDay = (dayIndex) => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days to subtract to get to Monday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysToMonday);
  
  // Add the day index (0 = Monday, 6 = Sunday)
  const targetDate = new Date(monday);
  targetDate.setDate(monday.getDate() + dayIndex);
  
  return targetDate.getDate(); // Return just the day number (15, 16, etc.)
};

const CalendarGrid = () => {
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  const { zoomLevel, draggedClass, dragOverSlot, selectedLocation } = useSelector((state) => state.calendar);
  const { classes } = useSelector((state) => state.classes);
  const touchMoveRef = useRef(null);
  const [dragGhostPosition, setDragGhostPosition] = useState(null);
  const weekRange = getCurrentWeekRange();

  // Filter classes based on selected location
  const filteredClasses = useMemo(() => {
    if (selectedLocation === null) {
      return classes;
    }
    return classes.filter((c) => c.location === selectedLocation);
  }, [classes, selectedLocation]);

  // Handle zoom with Ctrl/Cmd + scroll
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        dispatch(setZoomLevel(zoomLevel + delta));
      }
    };

    const calendar = calendarRef.current;
    if (calendar) {
      calendar.addEventListener('wheel', handleWheel, { passive: false });
      return () => calendar.removeEventListener('wheel', handleWheel);
    }
  }, [zoomLevel, dispatch]);

  // Initialize drag ghost position when drag starts
  useEffect(() => {
    if (draggedClass && !dragGhostPosition) {
      // If we have a recent touch position, use it; otherwise wait for touch move
      if (touchMoveRef.current) {
        setDragGhostPosition({
          x: touchMoveRef.current.x,
          y: touchMoveRef.current.y,
        });
      }
    } else if (!draggedClass) {
      setDragGhostPosition(null);
    }
  }, [draggedClass, dragGhostPosition]);

  // Handle touch events for mobile drag and drop
  useEffect(() => {
    const calendar = calendarRef.current;
    if (!calendar) return;

    const handleTouchMoveGlobal = (e) => {
      if (!draggedClass) {
        // Track touch position even when not dragging (for when drag starts)
        const touch = e.touches[0];
        touchMoveRef.current = { x: touch.clientX, y: touch.clientY };
        return;
      }
      
      const touch = e.touches[0];
      touchMoveRef.current = { x: touch.clientX, y: touch.clientY };
      
      // Update drag ghost position - center it on the touch point
      setDragGhostPosition({
        x: touch.clientX,
        y: touch.clientY,
      });
      
      // Find the element at touch point
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!element) return;
      
      // Find the slot element (look for parent with data attributes)
      let slotElement = element;
      while (slotElement && !slotElement.dataset.day) {
        slotElement = slotElement.parentElement;
      }
      
      if (slotElement && slotElement.dataset.day && slotElement.dataset.time) {
        const day = slotElement.dataset.day;
        const time = slotElement.dataset.time;
        
        // Calculate if we're in the bottom half of the slot (half-hour position)
        const rect = slotElement.getBoundingClientRect();
        const y = touch.clientY - rect.top;
        const slotHeight = rect.height;
        const isHalfHour = y > slotHeight / 2;
        
        // Determine the actual drop time (base hour or half-hour)
        const dropTime = isHalfHour ? getHalfHourTime(time) : time;
        
        const isValid = canDropAtSlot(day, dropTime, draggedClass);
        const currentSlot = dragOverSlot?.day === day && dragOverSlot?.time === time && dragOverSlot?.isHalfHour === isHalfHour;
        
        if (isValid && !currentSlot) {
          dispatch(setDragOverSlot({ day, time, isHalfHour, dropTime }));
        } else if (!isValid && currentSlot) {
          dispatch(setDragOverSlot(null));
        }
      }
    };

    const handleTouchEndGlobal = async (e) => {
      if (!draggedClass || !touchMoveRef.current) {
        touchMoveRef.current = null;
        return;
      }
      
      const touch = e.changedTouches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (!element) {
        dispatch(clearDraggedClass());
        dispatch(setDragOverSlot(null));
        touchMoveRef.current = null;
        return;
      }
      
      // Find the slot element
      let slotElement = element;
      while (slotElement && !slotElement.dataset.day) {
        slotElement = slotElement.parentElement;
      }
      
      if (slotElement && slotElement.dataset.day && slotElement.dataset.time) {
        const day = slotElement.dataset.day;
        const time = slotElement.dataset.time;
        
        // Calculate if we're in the bottom half of the slot (half-hour position)
        const rect = slotElement.getBoundingClientRect();
        const y = touch.clientY - rect.top;
        const slotHeight = rect.height;
        const isHalfHour = y > slotHeight / 2;
        
        // Determine the actual drop time (base hour or half-hour)
        const dropTime = isHalfHour ? getHalfHourTime(time) : time;
        
        // Check if drop is valid
        if (!canDropAtSlot(day, dropTime, draggedClass)) {
          dispatch(clearDraggedClass());
          dispatch(setDragOverSlot(null));
          touchMoveRef.current = null;
          return;
        }
        
        // Update the class with new day and time
        const updatedClass = {
          ...draggedClass,
          day,
          startTime: dropTime,
        };
        
        // Optimistic update (update UI immediately)
        dispatch(updateClassLocal(updatedClass));
        
        // Persist to backend
        const classId = draggedClass.id || draggedClass._id;
        if (classId) {
          await dispatch(updateClass({ 
            id: classId, 
            data: updatedClass 
          }));
        }
      }
      
      dispatch(clearDraggedClass());
      dispatch(setDragOverSlot(null));
      touchMoveRef.current = null;
      setDragGhostPosition(null);
    };

    calendar.addEventListener('touchmove', handleTouchMoveGlobal, { passive: false });
    calendar.addEventListener('touchend', handleTouchEndGlobal, { passive: false });
    
    return () => {
      calendar.removeEventListener('touchmove', handleTouchMoveGlobal);
      calendar.removeEventListener('touchend', handleTouchEndGlobal);
    };
  }, [draggedClass, dragOverSlot, filteredClasses, dispatch]);

  const handleClassClick = (classItem) => {
    dispatch(setSelectedClass(classItem));
  };

  // Get classes for a day (for weekday dots)
  const getClassesForDay = (day) => {
    return filteredClasses.filter((c) => c.day === day);
  };

  const handleEmptySlotClick = (day, time) => {
    // Create a new class template
    const newClass = {
      id: `new-${Date.now()}`,
      type: 'Bhangra',
      ageGroup: 'Teens',
      instructor: '',
      day,
      startTime: time,
      duration: 60,
      location: '',
      notes: '',
      rumaalLevel: 'White',
      students: [],
      isNew: true,
    };
    dispatch(setSelectedClass(newClass));
  };

  // Get classes for a specific day and time slot
  // This includes classes that start at this exact time, or classes that start at half-hour
  // times that belong to this base hour slot (e.g., 5:30 belongs to 5:00 slot)
  const getClassesForSlot = (day, time) => {
    return filteredClasses.filter((c) => {
      if (c.day !== day) return false;
      // Exact match
      if (c.startTime === time) return true;
      // Half-hour classes belong to their base hour slot
      if (isHalfHourTime(c.startTime) && getBaseHourSlot(c.startTime) === time) {
        return true;
      }
      return false;
    });
  };

  // Check if a slot has a class starting at that time (including half-hour times)
  const hasClassAtTime = (day, time) => {
    return filteredClasses.some((c) => {
      if (c.day !== day) return false;
      // Exact match
      if (c.startTime === time) return true;
      // Half-hour classes belong to their base hour slot
      if (isHalfHourTime(c.startTime) && getBaseHourSlot(c.startTime) === time) {
        return true;
      }
      return false;
    });
  };

  // Get classes that span this time slot (but don't start here)
  const getSpanningClasses = (day, time) => {
    return filteredClasses.filter((c) => {
      if (c.day !== day) return false;
      
      // Get the base hour slot for the class start time
      const classBaseSlot = isHalfHourTime(c.startTime) ? getBaseHourSlot(c.startTime) : c.startTime;
      if (classBaseSlot === time) return false; // Already handled by getClassesForSlot
      
      const startIndex = timeSlots.indexOf(classBaseSlot);
      if (startIndex === -1) return false;
      
      // Calculate duration in slots (accounting for half-hour offset)
      const durationSlots = c.duration / 60;
      // If class starts at half-hour, it effectively starts 0.5 slots into the base slot
      const effectiveStartIndex = startIndex + (isHalfHourTime(c.startTime) ? 0.5 : 0);
      const endIndex = effectiveStartIndex + durationSlots;
      const currentIndex = timeSlots.indexOf(time);
      
      // Check if this slot is within the class's time range
      return currentIndex >= startIndex && currentIndex < Math.ceil(endIndex);
    });
  };

  // Check if slot is occupied by any class (starting or spanning)
  const isSlotOccupied = (day, time) => {
    return hasClassAtTime(day, time) || getSpanningClasses(day, time).length > 0;
  };

  // Check if a class can be dropped at a specific slot
  // time can be either a base hour (e.g., "09:00") or a half-hour (e.g., "09:30")
  const canDropAtSlot = (day, time, classToDrop) => {
    if (!classToDrop) return false;
    
    // Get the actual start time (could be half-hour)
    const dropStartTime = time;
    
    // Prevent dropping at 23:30 (past midnight)
    if (dropStartTime === '23:30') return false;
    
    // Get the base hour slot for calculating slot indices
    const dropBaseSlot = isHalfHourTime(dropStartTime) ? getBaseHourSlot(dropStartTime) : dropStartTime;
    const startIndex = timeSlots.indexOf(dropBaseSlot);
    if (startIndex === -1) return false;
    
    // Calculate effective start considering half-hour offset
    const dropEffectiveStart = startIndex + (isHalfHourTime(dropStartTime) ? 0.5 : 0);
    const durationSlots = classToDrop.duration / 60;
    const dropEndIndex = dropEffectiveStart + durationSlots;
    
    // Check if the class would go past the last time slot (11PM)
    if (dropEndIndex > timeSlots.length) return false;
    
    // Check if there's already a class that would conflict (excluding the dragged class itself)
    const existingClasses = filteredClasses.filter(
      (c) => c.day === day && 
      c.id !== classToDrop.id && 
      c.id !== classToDrop._id
    );
    
    const hasConflict = existingClasses.some((c) => {
      // Get base hour slot for the existing class
      const cBaseSlot = isHalfHourTime(c.startTime) ? getBaseHourSlot(c.startTime) : c.startTime;
      const cStartIndex = timeSlots.indexOf(cBaseSlot);
      if (cStartIndex === -1) return false;
      
      // Calculate effective start considering half-hour offset
      const cEffectiveStart = cStartIndex + (isHalfHourTime(c.startTime) ? 0.5 : 0);
      const cDurationSlots = c.duration / 60;
      const cEndIndex = cEffectiveStart + cDurationSlots;
      
      // Check if time ranges overlap
      return !(dropEndIndex <= cEffectiveStart || dropEffectiveStart >= cEndIndex);
    });
    
    if (hasConflict) return false;
    
    return true;
  };

  const handleDragOver = (e, day, time) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedClass) {
      // Calculate if we're in the bottom half of the slot (half-hour position)
      const slotElement = e.currentTarget;
      const rect = slotElement.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const slotHeight = rect.height;
      const isHalfHour = y > slotHeight / 2;
      
      // Determine the actual drop time (base hour or half-hour)
      const dropTime = isHalfHour ? getHalfHourTime(time) : time;
      
      const isValid = canDropAtSlot(day, dropTime, draggedClass);
      const currentSlot = dragOverSlot?.day === day && dragOverSlot?.time === time && dragOverSlot?.isHalfHour === isHalfHour;
      
      // Only update if the slot changed
      if (isValid && !currentSlot) {
        dispatch(setDragOverSlot({ day, time, isHalfHour, dropTime }));
      } else if (!isValid && currentSlot) {
        dispatch(setDragOverSlot(null));
      }
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the calendar area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      dispatch(setDragOverSlot(null));
    }
  };

  const handleDrop = async (e, day, time) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedClass) return;
    
    // Calculate if we're in the bottom half of the slot (half-hour position)
    const slotElement = e.currentTarget;
    const rect = slotElement.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const slotHeight = rect.height;
    const isHalfHour = y > slotHeight / 2;
    
    // Determine the actual drop time (base hour or half-hour)
    const dropTime = isHalfHour ? getHalfHourTime(time) : time;
    
    // Check if drop is valid
    if (!canDropAtSlot(day, dropTime, draggedClass)) {
      dispatch(clearDraggedClass());
      dispatch(setDragOverSlot(null));
      return;
    }
    
    // Update the class with new day and time
    const updatedClass = {
      ...draggedClass,
      day,
      startTime: dropTime,
    };
    
    // Optimistic update (update UI immediately)
    dispatch(updateClassLocal(updatedClass));
    
    // Persist to backend
    const classId = draggedClass.id || draggedClass._id;
    if (classId) {
      await dispatch(updateClass({ 
        id: classId, 
        data: updatedClass 
      }));
    }
    
    dispatch(clearDraggedClass());
    dispatch(setDragOverSlot(null));
  };


  return (
    <div 
      ref={calendarRef}
      className="w-full h-screen overflow-auto bg-gray-50"
      style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left', touchAction: 'pan-y' }}
    >
      
      {/* Weekly Stats Dashboard */}
      <div className="flex-shrink-0">
        <WeeklyStats />
      </div>

      {/* Drag Ghost for Mobile Touch Drag */}
      {draggedClass && dragGhostPosition && (() => {
        const backgroundColor = getClassBackgroundColor(draggedClass.type, draggedClass.ageGroup);
        const borderColor = getClassBorderColor(draggedClass.type, draggedClass.ageGroup);
        const height = getDurationInSlots(draggedClass.duration) * 160;
        const studentCount = draggedClass.students?.length || 0;
        
        return (
          <div
            className="fixed pointer-events-none z-50"
            style={{
              left: `${dragGhostPosition.x}px`,
              top: `${dragGhostPosition.y}px`,
              transform: 'translate(-50%, -50%)',
              width: '180px',
              opacity: 0.85,
            }}
          >
            <div
              className="rounded-md overflow-hidden shadow-2xl"
              style={{
                backgroundColor,
                border: borderColor !== 'transparent' ? `2px solid ${borderColor}` : `1px solid rgba(0, 0, 0, 0.08)`,
                height: `${height}px`,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              }}
            >
              {/* Left accent stripe */}
              <div 
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{
                  backgroundColor: borderColor !== 'transparent' ? borderColor : getRumaalColor('White'),
                  opacity: 0.6,
                }}
              />
              
              {/* Content */}
              <div className="h-full flex flex-col p-2.5 relative">
                <div className="text-sm font-bold text-gray-900 truncate">
                  {draggedClass.name || `${draggedClass.type} – ${draggedClass.ageGroup}`}
                </div>
                {draggedClass.instructor && (
                  <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                    <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{draggedClass.instructor}</span>
                  </div>
                )}
                <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <span>{studentCount} {studentCount === 1 ? 'student' : 'students'}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Calendar Grid - Horizontal Scroll Container */}
      <div className="overflow-x-auto">
        <div className="min-w-full inline-block">
          {/* Header with days - Clean and minimal */}
          <div className="flex border-b border-gray-200 sticky top-0 bg-white z-10 shadow-sm min-w-max">
            {/* Week Date Range in the first cell - before Monday */}
            <div className="w-24 sm:w-28 md:w-36 flex-shrink-0 border-r border-gray-200 flex flex-col items-center justify-center bg-white px-1 py-2 sm:px-2 sm:py-2.5">
              <div className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide mb-0.5 sm:mb-1">
                Week
              </div>
              <div className="text-xs sm:text-sm md:text-base font-bold text-gray-900 text-center leading-tight px-1">
                {weekRange}
              </div>
            </div>
            {daysOfWeek.map((day, index) => {
              const dayDate = getDateForDay(index); // Get actual date number (15, 16, etc.)
              const dayClasses = getClassesForDay(day);
              const isWeekday = !isWeekend(day);
              
              // Subtle, refined gradient colors for each day
              const dayGradients = {
                'Monday': 'from-indigo-400 to-indigo-500',
                'Tuesday': 'from-pink-400 to-pink-500',
                'Wednesday': 'from-amber-400 to-amber-500',
                'Thursday': 'from-emerald-400 to-emerald-500',
                'Friday': 'from-blue-400 to-blue-500',
                'Saturday': 'from-violet-400 to-violet-500',
                'Sunday': 'from-rose-400 to-rose-500',
              };
              
              const gradient = dayGradients[day] || 'from-gray-400 to-gray-500';
              
              return (
                <div
                  key={day}
                  className={`flex-1 flex flex-col items-center justify-center border-r border-gray-200 min-w-[140px] sm:min-w-[160px] md:min-w-[180px] py-2 sm:py-2.5 transition-all duration-200 ${
                    isWeekend(day) ? 'bg-blue-50/40' : 'bg-white'
                  } hover:bg-gray-50/50`}
                >
                  {/* Subtle, refined day header */}
                  <div className="relative mb-1.5 sm:mb-2">
                    {/* Smaller gradient badge with subtle shadow - showing date number */}
                    <div 
                      className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-semibold text-sm sm:text-base md:text-lg shadow-md transform transition-all duration-200 hover:scale-105`}
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      {dayDate}
                    </div>
                  </div>
                  
                  {/* Full day name - subtle and elegant */}
                  <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-1.5 tracking-wide">
                    {day}
                  </div>
                  
                  {/* Subtle bottom accent line for weekdays - under the day name */}
                  {isWeekday && (
                    <div 
                      className={`mb-1.5 h-0.5 w-8 sm:w-10 bg-gradient-to-r ${gradient} rounded-full opacity-40`}
                    />
                  )}
                  
                  {/* Show dots for all days - each dot matches its class color */}
                  {dayClasses.length > 0 && (
                    <div className="flex items-center justify-center gap-1 flex-wrap max-w-full px-1">
                      {dayClasses.map((classItem, idx) => {
                        const classColor = getClassBackgroundColor(classItem.type, classItem.ageGroup);
                        return (
                          <div
                            key={classItem.id || classItem._id || idx}
                            className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-200 hover:scale-125"
                            style={{ 
                              backgroundColor: classColor,
                              minWidth: '4px', 
                              minHeight: '4px',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Time slots */}
          <div className="flex min-w-max">
            {/* Time column - Minimal */}
            <div className="w-24 sm:w-28 md:w-36 flex-shrink-0 border-r border-gray-200 bg-gray-50/30">
              {timeSlots.map((time) => (
                <div
                  key={time}
                  className="h-32 sm:h-36 md:h-40 border-b border-gray-100 flex items-center justify-center text-sm sm:text-base md:text-lg font-medium text-gray-500"
                >
                  {formatTime(time)}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="flex-1 flex min-w-max">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className={`flex-1 border-r border-gray-200 min-w-[140px] sm:min-w-[160px] md:min-w-[180px] ${
                    isWeekend(day) ? 'bg-blue-50/40' : 'bg-white'
                  }`}
                >
                  {timeSlots.map((time) => {
                    const isWeekdayMorningSlot = !isWeekend(day) && isWeekdayMorning(time);
                    const slotClasses = getClassesForSlot(day, time);
                    const spanningClasses = getSpanningClasses(day, time);

                    const isDragOver = dragOverSlot?.day === day && dragOverSlot?.time === time;
                    // Check if we're dragging over the half-hour position
                    const isHalfHourDrop = dragOverSlot?.isHalfHour === true;
                    const dropTime = isHalfHourDrop ? getHalfHourTime(time) : time;
                    const isValidDrop = draggedClass && canDropAtSlot(day, dropTime, draggedClass);
                    
                    return (
                      <div
                        key={`${day}-${time}`}
                        data-day={day}
                        data-time={time}
                        onDragOver={(e) => handleDragOver(e, day, time)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, day, time)}
                        className={`relative h-32 sm:h-36 md:h-40 transition-all duration-150 ${
                          isWeekdayMorningSlot 
                            ? 'bg-gray-200/50 border-b border-gray-300' 
                            : isWeekend(day)
                            ? 'border-b border-gray-300'
                            : 'border-b border-gray-100'
                        } ${
                          isDragOver && isValidDrop
                            ? isHalfHourDrop
                              ? 'bg-green-50 border-b-2 border-green-400 border-dashed'
                              : 'bg-green-50 border-2 border-green-400 border-dashed'
                            : ''
                        } ${
                          isDragOver && !isValidDrop
                            ? isHalfHourDrop
                              ? 'bg-red-50 border-b-2 border-red-400 border-dashed'
                              : 'bg-red-50 border-2 border-red-400 border-dashed'
                            : ''
                        }`}
                      >
                      {/* Half-hour divider - visible when dragging */}
                      {draggedClass && (
                        <div 
                          className="absolute left-0 right-0 border-t border-dashed border-gray-300 opacity-40 pointer-events-none"
                          style={{ top: '50%' }}
                        />
                      )}
                      {/* Render class blocks that start at this time */}
                      {slotClasses.map((classItem) => (
                        <ClassBlock
                          key={classItem.id}
                          classItem={classItem}
                          onClick={() => handleClassClick(classItem)}
                        />
                      ))}

                      {/* Render spanning classes (classes that continue through this slot) */}
                      {spanningClasses.length > 0 && (() => {
                        const spanningClass = spanningClasses[0];
                        // Get base hour slot for the class start time
                        const classBaseSlot = isHalfHourTime(spanningClass.startTime) ? getBaseHourSlot(spanningClass.startTime) : spanningClass.startTime;
                        const startIndex = timeSlots.indexOf(classBaseSlot);
                        const durationSlots = spanningClass.duration / 60;
                        // Account for half-hour offset in end calculation
                        const effectiveStart = startIndex + (isHalfHourTime(spanningClass.startTime) ? 0.5 : 0);
                        const endIndex = effectiveStart + durationSlots;
                        const currentIndex = timeSlots.indexOf(time);
                        
                        // Calculate how much of this slot the class occupies
                        // Responsive heights: h-32 (128px) on mobile, h-36 (144px) on sm, h-40 (160px) on md+
                        const getSlotHeight = () => {
                          const width = window.innerWidth;
                          if (width < 640) return 128; // Mobile
                          if (width < 768) return 144; // Small
                          return 160; // Medium+
                        };
                        
                        const baseHeight = getSlotHeight();
                        let spanHeight = baseHeight; // Default to full slot height
                        let spanTop = 0; // Default to top of slot
                        
                        if (currentIndex === startIndex && isHalfHourTime(spanningClass.startTime)) {
                          // This is the starting slot for a half-hour class
                          // The class starts halfway through this slot
                          spanTop = baseHeight / 2;
                          // If the class is short (e.g., 30 min), it might end in this same slot
                          if (endIndex <= startIndex + 1) {
                            spanHeight = (endIndex - effectiveStart) * baseHeight;
                          } else {
                            spanHeight = baseHeight / 2; // Half of this slot
                          }
                        } else if (currentIndex < Math.floor(endIndex)) {
                          // Class continues through this slot
                          if (currentIndex === Math.floor(endIndex) - 1 && endIndex % 1 !== 0) {
                            // Class ends partway through this slot (half-hour end)
                            spanHeight = (endIndex % 1) * baseHeight;
                          } else {
                            spanHeight = baseHeight;
                          }
                        } else {
                          // Should not render here
                          return null;
                        }
                        
                        return (
                          <ClassBlock
                            key={spanningClass.id}
                            classItem={spanningClass}
                            onClick={() => handleClassClick(spanningClass)}
                            isSpanning={true}
                            spanHeight={spanHeight}
                            spanTop={spanTop}
                          />
                        );
                      })()}

                      {/* Empty slot with + button */}
                      {!isSlotOccupied(day, time) && !draggedClass && (
                        <EmptySlot
                          day={day}
                          time={time}
                          onClick={() => handleEmptySlotClick(day, time)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarGrid;

